import { cn } from "@plasma/utils/cn";
// TODO: @radix-ui/react-slot has no @vortexnyc/ui equivalent — needed for polymorphic FormControl
import { Slot } from "@radix-ui/react-slot";
import { Info } from "lucide-react";
import { type ComponentProps, createContext, use, useId, useMemo } from "react";
import {
  Controller,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
  FormProvider,
  useFormContext,
} from "react-hook-form";

import { Label } from "~/components/ui/label";

const Form = FormProvider;

interface FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  name: TName;
}

const FormFieldContext = createContext<FormFieldContextValue | null>(null);

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  const value = useMemo(() => ({ name: props.name }), [props.name]);
  return (
    <FormFieldContext value={value}>
      <Controller {...props} />
    </FormFieldContext>
  );
};

const useFormField = () => {
  const fieldContext = use(FormFieldContext);
  const itemContext = use(FormItemContext);
  const { getFieldState, formState } = useFormContext();

  if (!fieldContext) {
    throw new Error("useFormField should be used within <FormField>");
  }

  if (!itemContext) {
    throw new Error("useFormField should be used within <FormItem>");
  }

  const fieldState = getFieldState(fieldContext.name, formState);

  const { id } = itemContext;

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  };
};

interface FormItemContextValue {
  id: string;
}

const FormItemContext = createContext<FormItemContextValue | null>(null);

function FormItem({ className, ref, ...props }: ComponentProps<"div">) {
  const id = useId();
  const value = useMemo(() => ({ id }), [id]);

  return (
    <FormItemContext value={value}>
      <div
        className={cn("flex flex-col items-start gap-2", className)}
        ref={ref}
        {...props}
      />
    </FormItemContext>
  );
}

function FormLabel({ className, ref, ...props }: ComponentProps<typeof Label>) {
  const { formItemId } = useFormField();

  return (
    <Label className={className} htmlFor={formItemId} ref={ref} {...props} />
  );
}

function FormControl({ ref, ...props }: ComponentProps<typeof Slot>) {
  const { error, formItemId, formDescriptionId, formMessageId } =
    useFormField();

  return (
    <Slot
      aria-describedby={
        error ? `${formDescriptionId} ${formMessageId}` : formDescriptionId
      }
      aria-invalid={!!error}
      id={formItemId}
      ref={ref}
      {...props}
    />
  );
}

function FormDescription({ className, ref, ...props }: ComponentProps<"p">) {
  const { formDescriptionId } = useFormField();

  return (
    <p
      className={cn("body-sm text-muted-foreground", className)}
      id={formDescriptionId}
      ref={ref}
      {...props}
    />
  );
}

function FormMessage({
  className,
  children,
  ref,
  ...props
}: ComponentProps<"p">) {
  const { error, formMessageId } = useFormField();
  const body = error ? String(error.message) : children;

  if (!body) {
    return null;
  }

  return (
    <p
      className={cn(
        "body-sm text-destructive dark:text-destructive flex gap-2",
        className
      )}
      id={formMessageId}
      ref={ref}
      {...props}
    >
      <Info className="text-destructive size-4" />
      {body}
    </p>
  );
}

export {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
};
